import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useOutletContext, useNavigate } from "react-router-dom";
import {
  MdOutlineEdit,
  MdOutlineLock,
  MdOutlineFolderOpen,
  MdOutlineLanguage,
  MdOutlineNotificationsNone,
  MdOutlineInfo,
  MdOutlineHelpOutline,
  MdOutlineWorkspacePremium,
  MdOutlineDescription,
  MdOutlinePolicy,
  MdOutlinePrivacyTip,
  MdOutlineTune,
  MdOutlineDeleteOutline,
  MdOutlineCreditCard,
  MdOutlineAccountBalance,
  MdOutlinePaid,
  MdOutlinePerson,
  MdChevronRight,
} from "react-icons/md";
import DashboardHeader from "../components/Other/DashboardHeader";
import { createConnectAccountApi, deleteAccount, getSettings } from "../api/user";
import ListSkeleton from "../components/Skeleton/ListSkeleton";
import { SkeletonBlock } from "../components/Skeleton/Skeleton";
import PageHeaderTitle from "../components/pages/PageHeaderTitle";
import PageContainer from "../components/pages/PageContainer";
import ConnectAccount from "./stripe-payment/bank-account/connect-account";
import Select from "react-select";
import { event } from "../utils/event";
import { toast } from "react-toastify";
import DeleteAccountModal from "../components/Modals/deleteAccountModal";
import DoNotSellModal from "../components/Modals/DoNotSellModal";
import PrivacyChoicesModal from "../components/Modals/PrivacyChoicesModal";
import { SUBSCRIPTION_ENABLED } from "../utils/utils";
import { openLegalLink } from "../utils/openLegalLink";
import { LEGAL_PRIVACY_URL, LEGAL_TERMS_URL } from "../utils/legalAcceptance";

// One consistent row: dark outline icon + label on the left, optional value and
// a chevron on the right. Renders a Link when `to` is set, otherwise a button.
function SettingsRow({ item }) {
  const Icon = item.Icon;
  const inner = (
    <>
      <span className="flex items-center gap-3">
        <Icon size={20} className="shrink-0 text-gray-700" />
        <span className="text-sm font-medium text-gray-800">{item.label}</span>
      </span>
      <span className="flex items-center gap-2">
        {item.value && (
          <span className="text-sm text-gray-400">{item.value}</span>
        )}
        {/* Rows without a chevron (e.g. Language) aren't links; the value stays
            right-aligned to line up with the other rows' chevrons. */}
        {!item.noChevron && (
          <MdChevronRight size={20} className="text-gray-400" />
        )}
      </span>
    </>
  );
  // Static rows (no `to` and no `onClick`, e.g. Language) are NOT links: no
  // pointer cursor, no hover, not focusable — they just display their value.
  const isStatic = !item.to && !item.onClick;
  const cls = `flex items-center justify-between rounded-lg -mx-2 px-2 py-2.5 transition-colors ${
    isStatic ? "" : "cursor-pointer hover:bg-gray-50"
  }`;
  if (item.to) {
    return (
      <Link to={item.to} className={cls}>
        {inner}
      </Link>
    );
  }
  if (item.onClick) {
    return (
      <div className={cls} role="button" tabIndex={0} onClick={item.onClick}>
        {inner}
      </div>
    );
  }
  return <div className={cls}>{inner}</div>;
}

function SettingsSection({ title, items }) {
  return (
    <section>
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
        {title}
      </p>
      <div>
        {items.map((item) => (
          <SettingsRow key={item.label} item={item} />
        ))}
      </div>
    </section>
  );
}

const Settings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sidebarToggle] = useOutletContext();

  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [isDataFeteched, setisDataFeteched] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState({
    value: "US",
    label: "USA",
  });
  const [stripeKey, setStripeKey] = useState("");
  const [connectOpen, setConnectOpen] = useState(null);
  const [status, setStatus] = useState({});
  const [openAcountModal, setOpenAcountModal] = useState(false);
  const [doNotSellOpen, setDoNotSellOpen] = useState(false);
  const [privacyChoicesOpen, setPrivacyChoicesOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

  const user_data = JSON.parse(localStorage.getItem("userInfo"));

  const fetchSetting = async () => {
    try {
      const response = await getSettings();
      if (response?.data?.data) {
        setisDataFeteched(true);
        setStripeKey(response.data.data.pk_key);
        setSettings(response.data.data);
        setLoading(false);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  useEffect(() => {
    if (!isDataFeteched) fetchSetting();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // The same Terms / Privacy URLs and opener as the signup checkbox. The
  // admin "APP" setting these used to come from held
  // https://qushi.io/terms_of_service, which does not exist (404).
  const redirectToTc = () => openLegalLink("terms_conditions", LEGAL_TERMS_URL);
  const redirectToPp = () => openLegalLink("privacy_policy", LEGAL_PRIVACY_URL);
  const accountDelete = () => setOpenAcountModal(true);
  const handleDeleteCancel = () => setOpenAcountModal(false);

  const handleDeleteAcount = async () => {
    try {
      const login_type = localStorage.getItem("google_login");
      if (login_type == "1") {
        event("sendEventForGoogleLogout");
      }
      const response = await deleteAccount();
      if (response) {
        setOpenAcountModal(false);
        toast.success("Your account has been deleted.");
        localStorage.clear();
        window.location.reload();
        navigate("/");
      } else {
        setOpenAcountModal(false);
      }
    } catch (error) {
      setOpenAcountModal(false);
      toast.error("Could not delete your account. Try again.");
      console.error("Error:", error);
    }
  };

  const handleAddAccountDetails = () => setConnectOpen(true);
  const handleCancelAccount = () => setConnectOpen(false);
  const handleUpdateAccount = () =>
    window.open(status?.new_onboarding_link, "_blank", "noopener,noreferrer");
  const handleCountryChange = (selectedOption) =>
    setSelectedCountry(selectedOption);

  // Monetization rows only exist when the subscription feature is enabled.
  const monetizationItems = SUBSCRIPTION_ENABLED
    ? [
        { label: "Payment", Icon: MdOutlineCreditCard, to: "/payment" },
        {
          label: "Add Bank Account",
          Icon: MdOutlineAccountBalance,
          onClick: handleAddAccountDetails,
          value: status?.onboarding_status === "completed" ? "Active" : "Inactive",
        },
        {
          label: "Paid Content",
          Icon: MdOutlinePaid,
          onClick: () => navigate(`/paid-content/${user_data?.id}`),
        },
      ]
    : [];

  // Section model — the single source rendered by both the mobile and desktop
  // layouts, so the two never drift apart.
  const accountItems = [
    { label: "Edit profile", Icon: MdOutlineEdit, to: "/edit-profile" },
    { label: "Reset password", Icon: MdOutlineLock, to: "/change-password" },
    { label: "Saved", Icon: MdOutlineFolderOpen, to: "/saved-post" },
    ...monetizationItems,
  ];
  const preferenceItems = [
    { label: "Language", Icon: MdOutlineLanguage, value: "English", noChevron: true },
    {
      label: "Notifications",
      Icon: MdOutlineNotificationsNone,
      // Preferences page; the header bell still opens the inbox at /notifications.
      to: "/settings/notifications",
    },
  ];
  const supportItems = [
    { label: "Page tips", Icon: MdOutlineInfo, to: "/guide" },
    { label: "Get help", Icon: MdOutlineHelpOutline, to: "/get-help" },
    { label: "About QUSHi", Icon: MdOutlineWorkspacePremium, to: "/about-qushi" },
  ];
  const legalItems = [
    {
      label: "Terms of Service",
      Icon: MdOutlineDescription,
      onClick: redirectToTc,
    },
    { label: "Privacy Policy", Icon: MdOutlinePolicy, onClick: redirectToPp },
    // Client, ticket 13083: "by law we need a way for users to change their
    // choice". The consent banner's two categories, on every surface - the
    // app's WebView included - unlike Do Not Sell below.
    {
      label: "Privacy Choices",
      Icon: MdOutlineTune,
      onClick: () => setPrivacyChoicesOpen(true),
    },
  ];
  const privacyChoiceItems = [
    {
      label: "Do Not Sell or Share My Personal Information",
      Icon: MdOutlinePrivacyTip,
      // Web app / qushi.io only — the Privacy Choices section is desktop-only,
      // so this never surfaces in the mobile app.
      onClick: () => setDoNotSellOpen(true),
    },
  ];
  const dangerItems = [
    {
      label: "Delete account",
      Icon: MdOutlineDeleteOutline,
      onClick: accountDelete,
    },
  ];

  const ProfileHeader = () => (
    <div className="flex items-center gap-4">
      {user_data?.image ? (
        <img
          src={user_data.image}
          alt=""
          className="h-16 w-16 shrink-0 rounded-full object-cover"
        />
      ) : (
        <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-gray-200 text-gray-500">
          <MdOutlinePerson size={34} />
        </span>
      )}
      <div className="min-w-0">
        <p className="truncate text-lg font-bold text-gray-900">
          {user_data?.user_name || user_data?.full_name}
        </p>
        {user_data?.title && (
          <p className="truncate text-sm text-secondary">{user_data.title}</p>
        )}
      </div>
    </div>
  );

  return (
    <>
      <DashboardHeader toggle={sidebarToggle} />
      {/* Desktop: no title text / grey line (the card carries its own header). */}
      <PageHeaderTitle title="Settings" showDesktopTitle={false} />
      <PageContainer>
        {loading ? (
          // Card chrome + row skeletons. The settings card, its border and the
          // page title were always static — only the profile row and the row
          // labels needed data.
          <div className="px-4 md:px-0 md:mt-4 mx-auto mb-6 max-w-4xl md:rounded-2xl md:border md:border-gray-200 md:bg-white md:p-8">
            <div className="flex items-center gap-4">
              <SkeletonBlock rounded="rounded-full" width={56} height={56} />
              <div className="flex-1 min-w-0">
                <SkeletonBlock height={14} width="40%" />
                <SkeletonBlock height={11} width="26%" className="mt-2" />
              </div>
            </div>
            <ListSkeleton
              rows={6}
              avatar={false}
              columns={0}
              className="mt-8"
              rowClassName="py-4"
            />
          </div>
        ) : (
          <>
            {/* ---------- Desktop: two-column card ---------- */}
            {!isMobileView && (
              <div className="hidden md:block md:mt-4 mx-auto mb-6 max-w-4xl rounded-2xl border border-gray-200 bg-white p-8">
                <ProfileHeader />

                <div className="mt-8 grid grid-cols-2 gap-x-12 gap-y-8">
                  <div className="space-y-8">
                    <SettingsSection title="Account" items={accountItems} />
                    <SettingsSection
                      title="Preferences"
                      items={preferenceItems}
                    />
                  </div>
                  <div className="space-y-8">
                    <SettingsSection title="Support" items={supportItems} />
                    {/* Web app / qushi.io only: "Do Not Sell…" sits with Terms,
                        Privacy and Privacy Choices at the bottom of the Legal
                        group (not on mobile). */}
                    <SettingsSection
                      title="Legal"
                      items={[...legalItems, ...privacyChoiceItems]}
                    />
                  </div>
                </div>

                <div className="mt-8 border-t border-gray-100 pt-8">
                  <SettingsSection title="Danger Zone" items={dangerItems} />
                </div>
              </div>
            )}

            {/* ---------- Mobile: single column, equal section rhythm ---------- */}
            {isMobileView && (
              <div className="px-4 pb-28">
                {/* Header block. Its bottom gap (mb-8) is the reference spacing,
                    carried between every section below via space-y-8. */}
                <div className="mb-8">
                  {/* House rule: sub-page title — title-subpage token (size),
                      Leaderboard font/weight, #004d4d. */}
                  <h1 className="mb-5 text-title-subpage font-bold text-[#004d4d]">
                    Settings
                  </h1>
                  <ProfileHeader />
                </div>

                <div className="space-y-8">
                  <SettingsSection title="Account" items={accountItems} />
                  <SettingsSection title="Preferences" items={preferenceItems} />
                  <SettingsSection title="Support" items={supportItems} />
                  <SettingsSection title="Legal" items={legalItems} />
                  <SettingsSection title="Danger Zone" items={dangerItems} />
                </div>
              </div>
            )}
          </>
        )}

        <ConnectAccount
          status={status?.onboarding_status}
          isOpen={connectOpen}
          handleUpdateAccount={handleUpdateAccount}
          handleCancelAccount={handleCancelAccount}
        >
          {status?.onboarding_status === "completed" ? (
            <>
              <p>Account Id : {status?.account_id}</p>
              <p>Status : {status?.onboarding_status}</p>
            </>
          ) : (
            <div className="mb-2">
              <Select
                value={selectedCountry}
                onChange={handleCountryChange}
                placeholder="Select Country"
              />
            </div>
          )}
        </ConnectAccount>

        <DeleteAccountModal
          isOpen={openAcountModal}
          handleDeleteCard={handleDeleteAcount}
          handleDeleteCancel={handleDeleteCancel}
        />

        <DoNotSellModal
          isOpen={doNotSellOpen}
          onClose={() => setDoNotSellOpen(false)}
        />

        {/* Mounted on open, so the switches always start from the stored
            choice as it is now. */}
        {privacyChoicesOpen && (
          <PrivacyChoicesModal
            isOpen
            onClose={() => setPrivacyChoicesOpen(false)}
          />
        )}
      </PageContainer>
    </>
  );
};

export default Settings;
